import React from 'react';
const { useState, useMemo } = React;
import { F } from '../../utils.jsx';
import { contactNameOf, newContactQuoteFields, newCompanyQuoteFields } from '../../personQuotes.js';

// «Nueva cotización» from «Cotizaciones y pagos»: one search over the contacts and companies that already exist.
// A person, a company, or both. With both, who contracts and pays is chosen explicitly (no default): the company
// (the person is its contact) or the person on her own (the company only as a reference, never the payer). Nothing
// is created besides the quote: the contact and the company are the existing records. Personal quotes are prepared
// only by the administration (the server checks it again). onCreate(fields) creates or reopens the draft.
const muted = { fontSize: 12, color: 'var(--globant-muted)' };
const norm = (v) => String(v || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
const box = { border: '1px solid var(--globant-border)', borderRadius: 10, padding: 10, display: 'grid', gap: 4, minWidth: 0 };

export default function NewQuoteForm({ stakeholders = [], accounts = [], canPersonal = false, busy = false, onCreate, onCancel }) {
  const [q, setQ] = useState('');
  const [contactId, setContactId] = useState('');
  const [accountId, setAccountId] = useState('');
  const [payer, setPayer] = useState(''); // '' | 'person' | 'company' (only asked with both)
  const [err, setErr] = useState('');
  const accName = (id) => F(accounts.find(a => a.id === id), 'Account Name') || '';
  const contact = stakeholders.find(s => s.id === contactId) || null;
  const matches = useMemo(() => {
    const w = norm(q).trim();
    if (!w) return [];
    const people = contactId ? [] : stakeholders.filter(s => norm(`${contactNameOf(s)} ${s.fields?.['Email'] || ''}`).includes(w)).slice(0, 6).map(s => ({ kind: 'person', id: s.id, label: contactNameOf(s) || '(sin nombre)', sub: [s.fields?.['Email'], (s.fields?.['Account'] || []).map(accName).filter(Boolean).join(', ')].filter(Boolean).join(' · ') }));
    const companies = accountId ? [] : accounts.filter(a => norm(F(a, 'Account Name')).includes(w)).slice(0, 6).map(a => ({ kind: 'company', id: a.id, label: F(a, 'Account Name'), sub: 'Empresa' }));
    return [...people, ...companies];
  }, [q, contactId, accountId, stakeholders, accounts]);
  const both = !!(contactId && accountId);
  // Only one choice is possible without asking: a person alone pays herself; a company alone pays itself
  const recipient = both ? payer : contactId ? 'person' : accountId ? 'company' : '';
  const related = both && (contact?.fields?.['Account'] || []).includes(accountId);
  const ready = recipient && (recipient !== 'person' || canPersonal);

  const pick = (m) => { if (m.kind === 'person') setContactId(m.id); else setAccountId(m.id); setQ(''); setPayer(''); };
  const submit = () => {
    setErr('');
    try {
      const fields = recipient === 'person'
        // The person pays on her own: the company goes only as a reference (never the payer)
        ? newContactQuoteFields({ stakeholderId: contactId, contactName: contactNameOf(contact), recipient: 'person', account: accountId ? { id: accountId, name: accName(accountId) } : null, linkAccount: !!accountId })
        : newCompanyQuoteFields({ account: { id: accountId, name: accName(accountId) }, contact: contactId ? { id: contactId, name: contactNameOf(contact) } : null });
      onCreate(fields);
    } catch (e) { setErr(e.message); }
  };

  return (
    <div className="card" style={{ marginBottom: 12, display: 'grid', gap: 10 }}>
      <b style={{ fontSize: 14 }}>Nueva cotización · ¿para quién es?</b>
      <div>
        <input className="input-field" style={{ fontSize: 12, width: '100%', maxWidth: 420 }} autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder={contactId && accountId ? 'Ya elegiste persona y empresa' : 'Buscar una persona o una empresa'} aria-label="Buscar persona o empresa" disabled={both} />
        {matches.map(m => (
          <button key={`${m.kind}-${m.id}`} type="button" className="action-btn btn-ghost" style={{ display: 'block', fontSize: 12, marginTop: 4, textAlign: 'left' }} onClick={() => pick(m)}>
            <b>{m.label}</b> <span style={muted}>· {m.kind === 'person' ? 'Persona' : 'Empresa'}{m.sub && m.kind === 'person' ? ` · ${m.sub}` : ''}</span>
          </button>
        ))}
        {q && !matches.length && <div style={muted}>Ningún contacto ni empresa con esa búsqueda. Primero creálo en Contactos o Empresas.</div>}
        <div style={{ ...muted, marginTop: 4 }}>Podés elegir una persona, una empresa o las dos.</div>
      </div>

      {(contactId || accountId) && (
        <div style={{ display: 'grid', gap: 8, gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
          {contactId && <div style={box}><span style={muted}>Persona</span><b>{contactNameOf(contact)}</b><button type="button" className="action-btn btn-ghost" style={{ fontSize: 11, justifySelf: 'start' }} onClick={() => { setContactId(''); setPayer(''); }}>Quitar</button></div>}
          {accountId && <div style={box}><span style={muted}>Empresa</span><b>{accName(accountId)}</b><button type="button" className="action-btn btn-ghost" style={{ fontSize: 11, justifySelf: 'start' }} onClick={() => { setAccountId(''); setPayer(''); }}>Quitar</button></div>}
        </div>
      )}

      {both && (
        <div style={{ display: 'grid', gap: 6 }}>
          <div style={{ fontSize: 13, fontWeight: 700 }}>¿Quién contrata y paga?</div>
          {!related && <div style={{ ...muted, color: '#9A6212' }}>Esta persona no figura como contacto de {accName(accountId)}. Revisá que sean las correctas.</div>}
          <label style={{ fontSize: 13, display: 'flex', gap: 6, alignItems: 'flex-start' }}>
            <input type="radio" name="nq-payer" checked={payer === 'company'} onChange={() => setPayer('company')} />
            <span><b>{accName(accountId)}</b> contrata y paga. {contactNameOf(contact)} es el contacto o beneficiaria: recibe la propuesta y puede responderla, no paga.</span>
          </label>
          <label style={{ fontSize: 13, display: 'flex', gap: 6, alignItems: 'flex-start', opacity: canPersonal ? 1 : 0.5 }}>
            <input type="radio" name="nq-payer" disabled={!canPersonal} checked={payer === 'person'} onChange={() => setPayer('person')} />
            <span><b>{contactNameOf(contact)}</b> contrata y paga por su cuenta. {accName(accountId)} queda solo como referencia: no paga ni recibe la cotización.</span>
          </label>
          <div style={muted}>Trabajar en una empresa no convierte una compra personal en empresarial: se elige siempre.</div>
        </div>
      )}
      {recipient === 'person' && !canPersonal && <div style={muted}>Las cotizaciones personales las prepara solo la administración.</div>}
      {recipient && (
        <div style={{ fontSize: 12 }}>Contrata y paga: <b>{recipient === 'person' ? contactNameOf(contact) : accName(accountId)}</b>{recipient === 'company' && contactId ? ` · contacto: ${contactNameOf(contact)}` : ''}{recipient === 'person' && accountId ? ` · referencia: ${accName(accountId)}` : ''}</div>
      )}
      {err && <div role="alert" style={{ fontSize: 12, color: '#B4362C' }}>{err}</div>}
      <div style={{ display: 'flex', gap: 6 }}>
        <button type="button" className="action-btn btn-primary" style={{ fontSize: 12 }} disabled={busy || !ready} onClick={submit}>{busy ? 'Creando…' : 'Crear y abrir la ficha'}</button>
        <button type="button" className="action-btn btn-ghost" style={{ fontSize: 12 }} disabled={busy} onClick={onCancel}>Cancelar</button>
      </div>
    </div>
  );
}
